import React, { useCallback } from "react";
import StatusBar from "@/ui/organisms/DealForm/StatusBar";
import Box from "@/ui/atoms/Box";
import type { BoxProps } from "@/ui/atoms/Box";
import type {
    DealNameCheck,
    PropertyAddressCheck,
    PropertyAddressCheckInfo,
    PropertyNameCheck,
    MultipropBaseDealInfo,
    MultipropBasePropertyInfo,
} from "@/ui/organisms/dealTypes";
import { CreateDealInfoForm } from "@/ui/organisms/DealForm/CreateDealInfoForm";
import { CreatePropertyList } from "@/ui/organisms/DealForm/CreatePropertyList";
import PropertyForm from "@/ui/organisms/DealForm/PropertyForm";

export type ViewDealCreationParams = {
    useState: (params: { databaseId: number }) => {
        currentStep: number;
        currentDeal: MultipropBaseDealInfo | null;
        currentProperties: Array<MultipropBasePropertyInfo>;
        editingProperty: {
            // Index of the property to edit in `properties`, or null if it doesn't exist
            index: number | null;
            // The property that's being edited.
            property: Partial<MultipropBasePropertyInfo>;
        } | null; // If we're editing a property, this says which one
        propertyAddressCheck: PropertyAddressCheck | null;
        availableCoAs: Array<{
            coaId: number;
            name: string;
        }> | null;
        availableCoAsStatus: "idle" | "pending" | "resolved" | "rejected";
        configGoogleMapsState: {
            status: "idle" | "pending" | "resolved" | "rejected";
            data: {
                apiKey: string;
            } | null;
        };
    };
    useCases: () => {
        decrementStep: () => void;
        nextStep: () => void;
        setDealNameCheck: (
            databaseId: number,
            dealName: string,
        ) => Promise<DealNameCheck>;
        setPropertyAddressCheck: (
            data: PropertyAddressCheckInfo,
        ) => Promise<PropertyAddressCheck>;
        setPropertyNameCheck: (
            databaseId: number,
            propertyName: string,
        ) => Promise<PropertyNameCheck>;
        onMount: ({ databaseId }: { databaseId: number }) => void;
        onUnmount: () => void;
        setDeal: (data: MultipropBaseDealInfo) => void;
        editNewProperty: () => void;
        updateEditingProperty: (
            data: Partial<MultipropBasePropertyInfo>,
        ) => void;
        editProperty: (propertyIndex: number) => void;
        saveEditingProperty: () => void;
        cancelEditingProperty: () => void;
        navigateToAppEntrypoint: () => void;
        removePropertyFromDeal: (propertyIndex: number) => void;
        confirmDeal: (databaseId: number) => Promise<void>;
    };
    getCurrentDate: () => Date;
};

export type ViewDealCreationProps = Omit<BoxProps, "children"> & {
    databaseId: string;
};

export const createViewDealCreation = ({
    useState,
    useCases,
    getCurrentDate,
}: ViewDealCreationParams) => {
    const ViewDealCreation = (props: ViewDealCreationProps) => {
        const { sx, databaseId: databaseIdFromProp, ...otherProps } = props;
        const databaseId = parseInt(databaseIdFromProp, 10);
        const {
            currentStep,
            currentDeal,
            currentProperties,
            editingProperty,
            propertyAddressCheck,
            configGoogleMapsState,
            availableCoAs,
            availableCoAsStatus,
        } = useState({ databaseId });
        const {
            decrementStep,
            nextStep,
            setDeal,
            editNewProperty,
            editProperty,
            updateEditingProperty,
            saveEditingProperty,
            cancelEditingProperty,
            setDealNameCheck,
            setPropertyAddressCheck,
            setPropertyNameCheck,
            navigateToAppEntrypoint,
            onMount,
            onUnmount,
            removePropertyFromDeal,
            confirmDeal,
        } = useCases();
        React.useEffect(() => {
            onMount({ databaseId });
            return onUnmount;
        }, [onMount, onUnmount, databaseId]);

        const handleMutateNewProperty = useCallback(
            (data: Partial<MultipropBasePropertyInfo>) =>
                updateEditingProperty(data),
            [updateEditingProperty],
        );

        return (
            <>
                <Box
                    sx={{
                        ...sx,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                    }}
                    {...otherProps}
                >
                    <StatusBar
                        currentStep={currentStep}
                        steps={["Create a Project", "Add Properties"]}
                        onExit={navigateToAppEntrypoint}
                    />
                    {currentStep === 1 && (
                        <CreateDealInfoForm
                            availableCoAs={availableCoAs ?? []}
                            availableCoAsStatus={availableCoAsStatus}
                            initialData={currentDeal ?? {}}
                            databaseId={databaseId}
                            onBack={navigateToAppEntrypoint}
                            onSubmit={async data => {
                                setDeal({ ...data });
                                nextStep();
                                return Promise.resolve();
                            }}
                            setDealNameCheck={setDealNameCheck}
                        />
                    )}
                    {currentStep === 2 && editingProperty === null && (
                        <CreatePropertyList
                            properties={currentProperties}
                            newProperty={editNewProperty}
                            editProperty={editProperty}
                            onRemoveFromListClicked={(propertyIndex: number) =>
                                void removePropertyFromDeal(propertyIndex)
                            }
                            onNext={() => confirmDeal(databaseId)}
                            onBack={decrementStep}
                        />
                    )}
                    {currentStep === 2 && editingProperty !== null && (
                        <PropertyForm<MultipropBasePropertyInfo>
                            property={editingProperty.property}
                            databaseId={databaseId}
                            onSubmit={async () => {
                                saveEditingProperty();
                                return Promise.resolve();
                            }}
                            onBack={cancelEditingProperty}
                            mutateNewProperty={handleMutateNewProperty}
                            setPropertyAddressCheck={setPropertyAddressCheck}
                            setPropertyNameCheck={setPropertyNameCheck}
                            propertyAddressCheck={propertyAddressCheck}
                            configGoogleMapsState={configGoogleMapsState}
                            getCurrentDate={getCurrentDate}
                        />
                    )}
                </Box>
            </>
        );
    };
    return ViewDealCreation;
};
